import React, { useState, useRef } from 'react';
import {
    View, Text, StyleSheet, FlatList, TextInput, TouchableOpacity,
    KeyboardAvoidingView, Platform, ActivityIndicator, Animated
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPostComments, addComment, toggleLike, repostPost } from '../../../api/social';
import PostCard from '../../../components/social/feed/PostCard';
import UserAvatar from '../../../components/social/shared/UserAvatar';
import EmojiPicker from 'rn-emoji-keyboard';

const formatCommentTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffSec = Math.floor((now - date) / 1000);
    if (diffSec < 60) return 'now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`;
    if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const toHandle = (name) => {
    if (!name) return '@user';
    return '@' + name.toLowerCase().replace(/\s+/g, '');
};

const HEADER_MAX_HEIGHT = 44;
const HEADER_MIN_HEIGHT = 0;

const PostDetailScreen = ({ route, navigation }) => {
    const { postId } = route.params;
    const insets = useSafeAreaInsets();
    const queryClient = useQueryClient();
    const [commentText, setCommentText] = useState('');
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

    // --- Header Animation Logic ---
    const scrollY = useRef(new Animated.Value(0)).current;

    // diffClamp for ultra-smooth follow-scroll
    const diffClamp = Animated.diffClamp(scrollY, 0, HEADER_MAX_HEIGHT);

    const headerTranslateY = diffClamp.interpolate({
        inputRange: [0, HEADER_MAX_HEIGHT],
        outputRange: [0, -HEADER_MAX_HEIGHT],
        extrapolate: 'clamp',
    });

    const headerOpacity = diffClamp.interpolate({
        inputRange: [0, HEADER_MAX_HEIGHT * 0.5],
        outputRange: [1, 0],
        extrapolate: 'clamp',
    });

    const feedData = queryClient.getQueryData(['feed', 1]);
    const post = feedData?.posts?.find(p => p.id === postId);

    const { data: comments, isLoading: isCommentsLoading } = useQuery({
        queryKey: ['postComments', postId],
        queryFn: () => getPostComments(postId)
    });

    const addCommentMutation = useMutation({
        mutationFn: ({ content }) => addComment(postId, content),
        onSuccess: () => {
            setCommentText('');
            queryClient.invalidateQueries(['postComments', postId]);
            queryClient.invalidateQueries(['feed']);
        }
    });

    const likeMutation = useMutation({
        mutationFn: toggleLike,
        onMutate: async (postId) => {
            await queryClient.cancelQueries({ queryKey: ['feed'] });
            await queryClient.cancelQueries({ queryKey: ['postComments', postId] });
            const previousFeed = queryClient.getQueryData(['feed', 1]);
            if (previousFeed) {
                queryClient.setQueryData(['feed', 1], old => {
                    if (!old) return old;
                    return {
                        ...old,
                        posts: old.posts.map(p => p.id === postId ? { ...p, isLiked: !p.isLiked, likesCount: p.isLiked ? Math.max(0, p.likesCount - 1) : p.likesCount + 1 } : p)
                    };
                });
            }
            return { previousFeed };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousFeed) {
                queryClient.setQueryData(['feed', 1], context.previousFeed);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries(['feed']);
            queryClient.invalidateQueries(['postComments', postId]);
        }
    });

    const repostMutation = useMutation({
        mutationFn: repostPost,
        onMutate: async (postId) => {
            await queryClient.cancelQueries({ queryKey: ['feed'] });
            const previousFeed = queryClient.getQueryData(['feed', 1]);
            if (previousFeed) {
                queryClient.setQueryData(['feed', 1], old => {
                    if (!old) return old;
                    return {
                        ...old,
                        posts: old.posts.map(p => p.id === postId ? { ...p, repostsCount: (p.repostsCount || 0) + 1 } : p)
                    };
                });
            }
            return { previousFeed };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousFeed) {
                queryClient.setQueryData(['feed', 1], context.previousFeed);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries(['feed']);
        }
    });

    const renderComment = ({ item, index }) => (
        <View style={[styles.commentRow, index === 0 && styles.commentRowFirst]}>
            {/* Avatar col */}
            <View style={styles.commentAvatarCol}>
                <UserAvatar source={item.user?.profileImageUrl} name={item.user?.name} size={38} />
                {/* thread line below avatar */}
                <View style={styles.threadLine} />
            </View>

            {/* Content col */}
            <View style={styles.commentContentCol}>
                {/* Author row */}
                <View style={styles.commentHeaderRow}>
                    <Text style={styles.commentName} numberOfLines={1}>{item.user?.name}</Text>
                    <Text style={styles.commentHandle} numberOfLines={1}>{toHandle(item.user?.name)}</Text>
                    <Text style={styles.commentDot}>·</Text>
                    <Text style={styles.commentTime}>{formatCommentTime(item.createdAt)}</Text>
                </View>

                {/* Comment text */}
                <Text style={styles.commentText}>{item.content}</Text>

                {/* Comment micro-actions */}
                <View style={styles.commentActions}>
                    <TouchableOpacity style={styles.commentAction}>
                        <Ionicons name="chatbubble-outline" size={16} color="#536471" />
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.commentAction}>
                        <Ionicons name="heart-outline" size={16} color="#536471" />
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.commentAction}>
                        <Ionicons name="repeat-outline" size={17} color="#536471" />
                    </TouchableOpacity>
                </View>
            </View>
        </View>
    );

    const renderHeader = () => (
        <View>
            {post ? (
                <PostCard
                    post={post}
                    isLiked={post.likes?.length > 0}
                    onLike={() => likeMutation.mutate(post.id)}
                    onComment={() => { /* already in thread */ }}
                    onShare={() => repostMutation.mutate(post.id)}
                    onProfilePress={(userId) => navigation.navigate('UserProfile', { userId })}
                    hideCommentButton={true}
                    showGrid={false}
                />
            ) : (
                <View style={styles.notFound}>
                    <Text style={styles.notFoundText}>Post not found</Text>
                </View>
            )}

            {/* Divider + Comments heading */}
            <View style={styles.commentsTitleRow}>
                <Text style={styles.commentsTitle}>
                    {comments?.length ? `${comments.length} Repl${comments.length === 1 ? 'y' : 'ies'}` : 'Replies'}
                </Text>
            </View>
        </View>
    );

    return (
        <View style={styles.container}>
            {/* 1. Fixed Status Bar Background */}
            <LinearGradient
                colors={['#0891b2', '#0369a1']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={[styles.statusBarBg, { height: insets.top }]}
            />

            {/* 2. Animated Header Content */}
            <Animated.View
                style={[
                    styles.fixedHeader,
                    {
                        top: insets.top,
                        transform: [{ translateY: headerTranslateY }],
                        opacity: headerOpacity,
                    }
                ]}
            >
                <LinearGradient
                    colors={['#0891b2', '#0369a1']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={{ height: HEADER_MAX_HEIGHT, justifyContent: 'center' }}
                >
                    <View style={styles.headerContent}>
                        <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
                            <Ionicons name="arrow-back" size={22} color="#FFFFFF" />
                        </TouchableOpacity>
                        <View style={styles.headerCenter}>
                            <Text style={styles.headerTitle}>Post</Text>
                        </View>
                    </View>
                </LinearGradient>
            </Animated.View>

            <KeyboardAvoidingView
                behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                style={{ flex: 1 }}
            >
                <Animated.FlatList
                    data={comments}
                    renderItem={renderComment}
                    keyExtractor={item => item.id.toString()}
                    ListHeaderComponent={renderHeader}
                    contentContainerStyle={[styles.listContent, { paddingTop: HEADER_MAX_HEIGHT + insets.top + 10 }]}
                    onScroll={Animated.event(
                        [{ nativeEvent: { contentOffset: { y: scrollY } } }],
                        { useNativeDriver: true }
                    )}
                    scrollEventThrottle={16}
                    ListEmptyComponent={
                        isCommentsLoading ? (
                            <ActivityIndicator style={{ marginTop: 24 }} color="#0891b2" />
                        ) : (
                            <View style={styles.emptyComments}>
                                <Ionicons name="chatbubbles-outline" size={44} color="#D1D5DB" />
                                <Text style={styles.emptyTitle}>No replies yet</Text>
                                <Text style={styles.emptyText}>Be the first to reply!</Text>
                            </View>
                        )
                    }
                />

                {/* Comment input bar */}
                <View style={[styles.inputBar, { paddingBottom: Math.max(insets.bottom, 12) }]}>
                    <TouchableOpacity style={{ marginRight: 8 }} onPress={() => setIsEmojiPickerOpen(true)}>
                        <Ionicons name="happy-outline" size={26} color="#9CA3AF" />
                    </TouchableOpacity>
                    <TextInput
                        style={styles.input}
                        placeholder="Post your reply"
                        placeholderTextColor="#9CA3AF"
                        value={commentText}
                        onChangeText={setCommentText}
                        multiline
                    />
                    <TouchableOpacity
                        style={[styles.replyBtn, !commentText.trim() && styles.replyBtnDisabled]}
                        onPress={() => addCommentMutation.mutate({ content: commentText })}
                        disabled={!commentText.trim() || addCommentMutation.isLoading}
                    >
                        {addCommentMutation.isLoading ? (
                            <ActivityIndicator size="small" color="#FFFFFF" />
                        ) : (
                            <Text style={styles.replyBtnText}>Reply</Text>
                        )}
                    </TouchableOpacity>
                </View>

                <EmojiPicker
                    onEmojiSelected={(emojiObject) => setCommentText(prev => prev + emojiObject.emoji)}
                    open={isEmojiPickerOpen}
                    onClose={() => setIsEmojiPickerOpen(false)}
                    enableSearchBar={true}
                    enableRecentlyUsed={true}
                />
            </KeyboardAvoidingView>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    statusBarBg: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 110,
    },
    fixedHeader: {
        position: 'absolute',
        left: 0,
        right: 0,
        zIndex: 100,
    },
    headerContent: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        height: HEADER_MAX_HEIGHT,
        gap: 12,
    },
    backBtn: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: 'rgba(255,255,255,0.15)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerCenter: { flex: 1 },
    headerTitle: {
        fontSize: 16,
        fontWeight: '800',
        color: '#FFFFFF',
    },

    listContent: {
        paddingBottom: 16,
    },
    notFound: {
        padding: 20,
        alignItems: 'center',
    },
    notFoundText: {
        color: '#9CA3AF',
        fontSize: 15,
    },
    commentsTitleRow: {
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: '#E1E8ED',
    },
    commentsTitle: {
        fontSize: 15,
        fontWeight: '700',
        color: '#0F1419',
    },

    // Comments
    commentRow: {
        flexDirection: 'row',
        paddingHorizontal: 14,
        paddingTop: 12,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: '#E1E8ED',
    },
    commentRowFirst: {
        borderTopWidth: 0,
    },
    commentAvatarCol: {
        width: 42,
        alignItems: 'center',
        marginRight: 10,
    },
    threadLine: {
        flex: 1,
        width: 2,
        backgroundColor: '#E1E8ED',
        borderRadius: 1,
        marginTop: 4,
        marginBottom: 4,
    },
    commentContentCol: {
        flex: 1,
        paddingBottom: 12,
    },
    commentHeaderRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        flexWrap: 'nowrap',
        overflow: 'hidden',
        marginBottom: 2,
    },
    commentName: {
        fontSize: 14,
        fontWeight: '700',
        color: '#0F1419',
        maxWidth: 110,
        flexShrink: 0,
    },
    commentHandle: {
        fontSize: 13,
        color: '#536471',
        flexShrink: 1,
    },
    commentDot: {
        fontSize: 13,
        color: '#536471',
    },
    commentTime: {
        fontSize: 13,
        color: '#536471',
        flexShrink: 0,
    },
    commentText: {
        fontSize: 15,
        color: '#0F1419',
        lineHeight: 21,
        marginTop: 2,
    },
    commentActions: {
        flexDirection: 'row',
        marginTop: 8,
        gap: 20,
    },
    commentAction: {
        padding: 2,
    },

    // Input bar
    inputBar: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 14,
        paddingTop: 10,
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: '#E1E8ED',
        gap: 10,
    },
    input: {
        flex: 1,
        backgroundColor: '#F3F4F6',
        borderRadius: 22,
        paddingHorizontal: 16,
        paddingVertical: 9,
        fontSize: 15,
        maxHeight: 100,
        color: '#0F1419',
    },
    replyBtn: {
        backgroundColor: '#1D9BF0',
        paddingHorizontal: 18,
        paddingVertical: 8,
        borderRadius: 20,
        minWidth: 64,
        alignItems: 'center',
    },
    replyBtnDisabled: {
        backgroundColor: '#93C5FD',
        opacity: 0.6,
    },
    replyBtnText: {
        color: '#FFFFFF',
        fontWeight: '700',
        fontSize: 14,
    },

    // Empty state
    emptyComments: {
        alignItems: 'center',
        paddingTop: 40,
        opacity: 0.8,
    },
    emptyTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: '#374151',
        marginTop: 10,
    },
    emptyText: {
        fontSize: 13,
        color: '#9CA3AF',
        marginTop: 4,
    },
});

export default PostDetailScreen;
